import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Frequency — continuous-control hold-in-band, redesigned for 3D depth
 * + engagement. Game 11 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: 20 Hz value samples + band parameters
 * from seed. Replay validates unchanged. Visual layer rebuilt: 3D ribbon
 * band, sphere tuner with trail, scope view, audio detune in/out of band.
 */

export const RUN_MS = 60_000;
const SAMPLE_INTERVAL_MS = 50;
const TUNER_SPEED = 1.6;
const WIDTH_MIN = 0.1;
const WIDTH_MAX = 0.3;
const Y_RANGE = 7;
/** v5 hard mode: secondary band visible/scored only in the last 15s.
 *  Mirrors scoring-frequency.ts HARD_MODE_WINDOW_MS — duplicated here
 *  to avoid the client pulling the scoring module just for one number. */
const HARD_MODE_WINDOW_MS = 15_000;

export interface FrequencySample { t: number; value: number }

export interface FrequencyCallbacks {
  onHud: (runMs: number, insideCount: number, scoreEstimate: number) => void;
  onGameOver: (result: { samples: FrequencySample[]; scoreEstimate: number; bestStreak: number; insideCount: number; }) => void;
}

/** Session-best ghost: sample trace from the highest-scoring run this
 *  browser tab has seen. Per-seed runs are fresh so "best on this seed"
 *  is meaningless — best-across-session-runs is. The needle's y at each
 *  time-in-run is rendered as a faint horizontal line so the player can
 *  race their own session high-water mark. */
export interface FrequencyGhost { samples: FrequencySample[] }

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface BandParams { centerSines: { freq: number; phase: number; amp: number }[]; widthSines: { freq: number; phase: number; amp: number }[]; }

function bandParamsFromSeed(seed: number): BandParams {
  const rng = makeRng(seed);
  return {
    centerSines: [
      { freq: 0.08 + rng() * 0.05, phase: rng() * Math.PI * 2, amp: 0.30 + rng() * 0.15 },
      { freq: 0.18 + rng() * 0.10, phase: rng() * Math.PI * 2, amp: 0.20 + rng() * 0.12 },
      { freq: 0.40 + rng() * 0.20, phase: rng() * Math.PI * 2, amp: 0.10 + rng() * 0.08 },
    ],
    widthSines: [
      { freq: 0.05 + rng() * 0.03, phase: rng() * Math.PI * 2, amp: 0.08 + rng() * 0.05 },
      { freq: 0.15 + rng() * 0.08, phase: rng() * Math.PI * 2, amp: 0.04 + rng() * 0.04 },
    ],
  };
}

/** Mirror of scoring-frequency.band2ParamsFromSeed — must stay in sync. */
function band2ParamsFromSeed(seed: number): BandParams {
  return bandParamsFromSeed(((seed ^ 0x5a5a5a5a) >>> 0) & 0x7fffffff);
}

/** Interpolate the ghost-trace tuner value at time `t_ms`. Samples are
 *  monotonically increasing in `t`. Returns null if `t` is past the
 *  trace's end (ghost ran out of time before the current run). */
function ghostValueAt(samples: FrequencySample[], t_ms: number): number | null {
  if (samples.length === 0) return null;
  if (t_ms <= samples[0].t) return samples[0].value;
  if (t_ms >= samples[samples.length - 1].t) return null;
  let lo = 0, hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].t <= t_ms) lo = mid; else hi = mid;
  }
  const a = samples[lo], b = samples[hi];
  const span = b.t - a.t;
  if (span <= 0) return a.value;
  const f = (t_ms - a.t) / span;
  return a.value + (b.value - a.value) * f;
}

function bandAt(params: BandParams, t_ms: number): { center: number; width: number } {
  const t = t_ms / 1000;
  let center = 0;
  for (const s of params.centerSines) center += s.amp * Math.sin(2 * Math.PI * s.freq * t + s.phase);
  center = Math.max(-0.8, Math.min(0.8, center));
  let width = 0.18;
  for (const s of params.widthSines) width += s.amp * Math.sin(2 * Math.PI * s.freq * t + s.phase);
  width = Math.max(WIDTH_MIN, Math.min(WIDTH_MAX, width));
  return { center, width };
}

export class Frequency {
  private engine: Engine;
  private audio = new AudioBed();
  private bandParams: BandParams;
  private bandMesh: THREE.Mesh;
  private bandMat: THREE.MeshStandardMaterial;
  private bandTrail: THREE.LineSegments;
  private bandTrailGeo: THREE.BufferGeometry;
  private bandHistory: number[] = []; // last N (center,t) for trail
  private tunerMesh: THREE.Mesh;
  private tunerMat: THREE.MeshStandardMaterial;
  private tunerHalo: THREE.Mesh;
  private tunerHaloMat: THREE.MeshBasicMaterial;
  private tunerTrail: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; }[] = [];
  private tunerTrailGeo: THREE.SphereGeometry;
  private tunerValue = 0;
  private inputUp = false;
  private inputDown = false;
  private pointerActive = false;
  private pointerY = 0;
  private startedAt = 0;
  private samples: FrequencySample[] = [];
  private streak = 0;
  private bestStreak = 0;
  private insideCount = 0;
  private scoreEstimate = 0;
  private samplerTimer = 0;
  private gameOver = false;
  private cb: FrequencyCallbacks;
  private canvas: HTMLCanvasElement;
  private envLight: THREE.PointLight;
  private ghost: FrequencyGhost | null = null;
  private ghostLine: THREE.Line | null = null;
  private ghostMat: THREE.LineBasicMaterial | null = null;
  private hardMode = false;
  private bandParams2: BandParams | null = null;
  private bandMesh2: THREE.Mesh | null = null;
  private bandMat2: THREE.MeshStandardMaterial | null = null;

  // v6 — three parallax starfield layers (near / mid / far) so the
  // ribbon reads as suspended in a scope's nighttime workshop, not on
  // a flat panel. Per memory feedback-sibling-games-shallow: depth cues
  // were absent. Modest density / cool palette keeps focus on the band.
  private starLayers: THREE.Points[] = [];

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") this.inputUp = true;
    if (e.code === "ArrowDown" || e.code === "KeyS") this.inputDown = true;
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowUp" || e.code === "KeyW") this.inputUp = false;
    if (e.code === "ArrowDown" || e.code === "KeyS") this.inputDown = false;
  };
  private onPointerDown = (e: PointerEvent) => { this.pointerActive = true; this.updatePointer(e); };
  private onPointerMove = (e: PointerEvent) => { if (this.pointerActive) this.updatePointer(e); };
  private onPointerUp = () => { this.pointerActive = false; };

  constructor(canvas: HTMLCanvasElement, cb: FrequencyCallbacks, seed = Math.floor(Math.random() * 0x7fffffff), opts: { ghost?: FrequencyGhost | null; hardMode?: boolean } = {}) {
    this.canvas = canvas;
    this.cb = cb;
    this.bandParams = bandParamsFromSeed(seed);
    this.ghost = opts.ghost ?? null;
    this.hardMode = opts.hardMode === true;
    if (this.hardMode) this.bandParams2 = band2ParamsFromSeed(seed);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    // Side-on 3D perspective: camera angled slightly.
    this.engine.camera.position.set(8, 1, 14);
    this.engine.camera.lookAt(0, 0, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x040814, 14, 36);
    this.engine.scene.background = new THREE.Color(0x040814);

    const ambient = new THREE.AmbientLight(0x506080, 0.45);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.9);
    key.position.set(8, 10, 10);
    this.engine.scene.add(key);
    this.envLight = new THREE.PointLight(0x66ddff, 0.8, 18, 1.5);
    this.envLight.position.set(0, 0, 4);
    this.engine.scene.add(this.envLight);

    // Backplate — a wide receding panel with scale tick marks for depth read.
    const backGeo = new THREE.PlaneGeometry(28, Y_RANGE * 2.4);
    const backMat = new THREE.MeshStandardMaterial({ color: 0x040810, roughness: 0.95, metalness: 0.15 });
    const back = new THREE.Mesh(backGeo, backMat);
    back.position.set(0, 0, -3);
    this.engine.scene.add(back);

    // Horizontal scale lines (every 2 y units → value increments).
    const tickPts: number[] = [];
    for (let v = -1; v <= 1; v += 0.2) {
      const y = v * Y_RANGE;
      tickPts.push(-14, y, -2.95, 14, y, -2.95);
    }
    const tickGeo = new THREE.BufferGeometry();
    tickGeo.setAttribute("position", new THREE.Float32BufferAttribute(tickPts, 3));
    const tickMat = new THREE.LineBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending });
    this.engine.scene.add(new THREE.LineSegments(tickGeo, tickMat));

    // Vertical axis pillar.
    const axisGeo = new THREE.BoxGeometry(0.1, Y_RANGE * 2 + 1, 0.1);
    const axisMat = new THREE.MeshBasicMaterial({ color: 0x88aaee, transparent: true, opacity: 0.45, blending: THREE.AdditiveBlending });
    const axis = new THREE.Mesh(axisGeo, axisMat);
    axis.position.set(0, 0, 0);
    this.engine.scene.add(axis);

    // Band — 3D box ribbon.
    const bandGeo = new THREE.BoxGeometry(7, 1, 0.6);
    this.bandMat = new THREE.MeshStandardMaterial({
      color: 0x00e0ff,
      emissive: 0x0080cc,
      emissiveIntensity: 0.8,
      roughness: 0.4,
      metalness: 0.5,
      transparent: true,
      opacity: 0.55,
    });
    this.bandMesh = new THREE.Mesh(bandGeo, this.bandMat);
    this.engine.scene.add(this.bandMesh);

    if (this.hardMode) {
      // Second band — same geometry, warmer tint so the player can tell
      // them apart at a glance. Hidden until the last 15s window.
      this.bandMat2 = new THREE.MeshStandardMaterial({
        color: 0xff9c40,
        emissive: 0xcc5500,
        emissiveIntensity: 0.7,
        roughness: 0.4,
        metalness: 0.5,
        transparent: true,
        opacity: 0.55,
      });
      this.bandMesh2 = new THREE.Mesh(bandGeo, this.bandMat2);
      this.bandMesh2.visible = false;
      this.engine.scene.add(this.bandMesh2);
    }

    // Band-center history trail (sequence of segments on the backplate).
    const trailCount = 120;
    const trailPos = new Float32Array(trailCount * 6);
    this.bandTrailGeo = new THREE.BufferGeometry();
    this.bandTrailGeo.setAttribute("position", new THREE.BufferAttribute(trailPos, 3));
    const trailMat = new THREE.LineBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending });
    this.bandTrail = new THREE.LineSegments(this.bandTrailGeo, trailMat);
    this.engine.scene.add(this.bandTrail);

    // Tuner — bright sphere.
    const tGeo = new THREE.SphereGeometry(0.45, 18, 14);
    this.tunerMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 1.6, roughness: 0.25, metalness: 0.7 });
    this.tunerMesh = new THREE.Mesh(tGeo, this.tunerMat);
    this.engine.scene.add(this.tunerMesh);

    const haloGeo = new THREE.RingGeometry(0.7, 1.05, 32);
    this.tunerHaloMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.tunerHalo = new THREE.Mesh(haloGeo, this.tunerHaloMat);
    this.engine.scene.add(this.tunerHalo);

    this.tunerTrailGeo = new THREE.SphereGeometry(0.18, 8, 6);

    if (this.ghost && this.ghost.samples.length >= 2) {
      const pts = new Float32Array([
        -11, 0, -0.4,
         11, 0, -0.4,
      ]);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pts, 3));
      this.ghostMat = new THREE.LineBasicMaterial({
        color: 0xffd884,
        transparent: true,
        opacity: 0.28,
        blending: THREE.AdditiveBlending,
      });
      this.ghostLine = new THREE.Line(geo, this.ghostMat);
      this.ghostLine.visible = false; // unhide on first frame once positioned
      this.engine.scene.add(this.ghostLine);
    }

    // v6 — parallax starfield. Three THREE.Points layers at different Z
    // depths give the scope view real distance instead of a flat backplate.
    // Modest density (350/180/80) and a cool palette keep the player's
    // attention on the band — stars are atmosphere, not focal. Seed-derived
    // positions so the same pattern re-appears per-run (matches the seed →
    // run philosophy elsewhere). Materials are fog:false so the far layer
    // still reads past the FogExp2 falloff. Per memory
    // feedback-sibling-games-shallow §"real 3D scene with depth cues".
    const makeStars = (count: number, z: number, radius: number, sizes: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: sizes.color, size: sizes.size, sizeAttenuation: true,
        transparent: true, opacity: 0.78, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(350, -200, 240, { size: 0.5,  color: 0x8092b0 }); // far — cool gray-blue
    makeStars(180, -110, 140, { size: 0.4,  color: 0xa0a8c8 }); // mid
    makeStars( 80,  -55,  90, { size: 0.35, color: 0xc0c8e0 }); // near — slightly brighter

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  }

  private updatePointer(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    this.pointerY = Math.max(-1, Math.min(1, ny));
  }

  start() {
    this.audio.arm();
    // v6 — baseline drone; frame() then adapts the intensity per the
    // current in-band streak so the bed thickens as the player holds
    // the band. Replaces the old fixed 0.45 seed. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    this.audio.setDroneIntensity(0.15);
    // Seed the sustained chime at the "outside" level so the first
    // sampler tick is a smooth lift rather than a click-on.
    this.audio.setChimeGain(0.2, 200);
    this.startedAt = performance.now();
    this.tunerValue = 0;
    this.samples = [];
    this.streak = 0;
    this.bestStreak = 0;
    this.insideCount = 0;
    this.scoreEstimate = 0;
    this.gameOver = false;
    this.bandHistory = [];
    for (const t of this.tunerTrail) {
      this.engine.scene.remove(t.mesh);
      t.mat.dispose();
    }
    this.tunerTrail = [];
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    this.samplerTimer = window.setInterval(() => this.takeSample(), SAMPLE_INTERVAL_MS);
    this.engine.start();
  }

  private takeSample() {
    if (this.gameOver) return;
    const t = performance.now() - this.startedAt;
    if (t > RUN_MS + 50) { this.finish(); return; }
    const value = Math.round(this.tunerValue / 0.01) * 0.01;
    this.samples.push({ t: Math.round(t), value: Math.max(-1, Math.min(1, value)) });
    const band = bandAt(this.bandParams, t);
    let insideBand = Math.abs(value - band.center) <= band.width;
    // v5: hard-mode secondary band — only checked in the last 15s.
    if (this.bandParams2 && t >= RUN_MS - HARD_MODE_WINDOW_MS) {
      const band2 = bandAt(this.bandParams2, t);
      if (Math.abs(value - band2.center) <= band2.width) insideBand = true;
    }
    if (insideBand) {
      this.insideCount += 1;
      this.streak += 1;
      if (this.streak > this.bestStreak) this.bestStreak = this.streak;
      const mult = Math.min(2.0, 1 + this.streak * 0.005);
      this.scoreEstimate += Math.round(50 * mult);
    } else {
      this.streak = 0;
    }
    // Continuous chime modulation — chime rides at full presence when
    // the tuner sits inside the band, ducks to 0.2 when it slips. The
    // 100 ms smoothing matches the dossier §3 spec.
    this.audio.setChimeGain(insideBand ? 1.0 : 0.2, 100);
    this.cb.onHud(t, this.insideCount, this.scoreEstimate);
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;
    const now = performance.now() - this.startedAt;

    if (this.pointerActive) {
      this.tunerValue = this.pointerY;
    } else {
      const dir = (this.inputUp ? 1 : 0) - (this.inputDown ? 1 : 0);
      this.tunerValue += dir * TUNER_SPEED * dt;
      this.tunerValue = Math.max(-1, Math.min(1, this.tunerValue));
    }

    const tunerY = this.tunerValue * Y_RANGE;
    this.tunerMesh.position.set(0, tunerY, 0);
    this.tunerHalo.position.set(0, tunerY, -0.05);

    const band = bandAt(this.bandParams, now);
    const bandY = band.center * Y_RANGE;
    const bandH = band.width * 2 * Y_RANGE;
    this.bandMesh.position.set(0, bandY, -1);
    this.bandMesh.scale.set(1, bandH, 1);

    let inside = Math.abs(this.tunerValue - band.center) <= band.width;
    // v5: render and consider the secondary band in the last 15s window.
    if (this.bandMesh2 && this.bandParams2 && now >= RUN_MS - HARD_MODE_WINDOW_MS) {
      const b2 = bandAt(this.bandParams2, now);
      this.bandMesh2.visible = true;
      this.bandMesh2.position.set(0, b2.center * Y_RANGE, -1);
      this.bandMesh2.scale.set(1, b2.width * 2 * Y_RANGE, 1);
      if (Math.abs(this.tunerValue - b2.center) <= b2.width) inside = true;
    } else if (this.bandMesh2) {
      this.bandMesh2.visible = false;
    }
    // v3: bonus window every 12s for 2s — band glows gold, score 2x.
    const inBonus = now >= 12_000 && (now % 12_000) < 2_000;
    if (inBonus) {
      this.bandMat.color.setHex(0xffd54a);
      this.bandMat.emissive.setHex(0xff8800);
      this.bandMat.emissiveIntensity = 1.8 + Math.min(1, this.streak / 100) * 1.0;
    } else if (inside) {
      this.bandMat.color.setHex(0x46ffe0);
      this.bandMat.emissive.setHex(0x00aa90);
      this.bandMat.emissiveIntensity = 1.0 + Math.min(1, this.streak / 100) * 1.4;
    } else {
      this.bandMat.color.setHex(0xff3df0);
      this.bandMat.emissive.setHex(0xcc1aa0);
      this.bandMat.emissiveIntensity = 0.5;
    }

    // Tuner appearance.
    if (inside) {
      this.tunerMat.color.setHex(0xffffff);
      this.tunerMat.emissive.setHex(0x46ffe0);
      this.tunerHaloMat.color.setHex(0x46ffe0);
      this.tunerHaloMat.opacity = 0.6 + Math.min(0.3, this.streak / 200);
    } else {
      this.tunerMat.color.setHex(0xffaa88);
      this.tunerMat.emissive.setHex(0xff3df0);
      this.tunerHaloMat.color.setHex(0xff3df0);
      this.tunerHaloMat.opacity = 0.4;
    }

    // Tuner trail — spawn a fading sphere every ~3 frames.
    if (this.tunerTrail.length < 40 && Math.random() < 0.5) {
      const mat = new THREE.MeshBasicMaterial({ color: inside ? 0x46ffe0 : 0xff3df0, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending });
      const m = new THREE.Mesh(this.tunerTrailGeo, mat);
      m.position.copy(this.tunerMesh.position);
      m.position.z = 0.2;
      this.engine.scene.add(m);
      this.tunerTrail.push({ mesh: m, mat });
    }
    for (let i = this.tunerTrail.length - 1; i >= 0; i--) {
      const t = this.tunerTrail[i];
      t.mat.opacity -= dt * 1.6;
      t.mesh.scale.multiplyScalar(1 - dt * 0.35);
      if (t.mat.opacity <= 0.01) {
        this.engine.scene.remove(t.mesh);
        t.mat.dispose();
        this.tunerTrail.splice(i, 1);
      }
    }

    // Band-center scrolling history line (left → right reading).
    this.bandHistory.push(bandY);
    if (this.bandHistory.length > 120) this.bandHistory.shift();
    const arr = this.bandTrailGeo.attributes.position.array as Float32Array;
    const len = this.bandHistory.length;
    for (let i = 0; i < len - 1; i++) {
      arr[i * 6 + 0] = -10 + (i / 120) * 14;
      arr[i * 6 + 1] = this.bandHistory[i];
      arr[i * 6 + 2] = -2.94;
      arr[i * 6 + 3] = -10 + ((i + 1) / 120) * 14;
      arr[i * 6 + 4] = this.bandHistory[i + 1];
      arr[i * 6 + 5] = -2.94;
    }
    this.bandTrailGeo.setDrawRange(0, Math.max(0, (len - 1) * 2));
    this.bandTrailGeo.attributes.position.needsUpdate = true;

    // Session-best ghost line — interpolate ghost.value at current `now`
    // and ride a faint horizontal line at that y. The trace is monotonic
    // in t, so a 2-step search from the last-known index is cheap.
    if (this.ghostLine && this.ghost) {
      const ghostY = ghostValueAt(this.ghost.samples, now);
      if (ghostY === null) {
        this.ghostLine.visible = false;
      } else {
        this.ghostLine.visible = true;
        const arr = (this.ghostLine.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        arr[1] = ghostY * Y_RANGE;
        arr[4] = ghostY * Y_RANGE;
        (this.ghostLine.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      }
    }

    // Env light tracks streak — getting tighter as the player stays in band.
    this.envLight.intensity = 0.6 + Math.min(1, this.streak / 60) * 1.8;
    this.envLight.color.setHex(inside ? 0x46ffe0 : 0xff3df0);
    this.envLight.position.set(0, tunerY, 4);

    // v6 — adaptive audio drone tracks the in-band streak so the bed
    // thickens as the player demonstrates consistent control. 120 samples
    // (~6 s of unbroken hold) saturates the ramp. Capped at 0.7 — full
    // intensity is reserved for game-over moments. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    // Note: NOT using sample_dt_stdev_ms or any timing-derived signal —
    // those are cross-run identifying per the telemetry prompts doc.
    const droneSignal = Math.min(1, this.streak / 120);
    try { this.audio.setDroneIntensity(0.15 + droneSignal * 0.55); } catch { /* off */ }

    if (now > RUN_MS) this.finish();
  }

  private finish() {
    if (this.gameOver) return;
    this.gameOver = true;
    clearInterval(this.samplerTimer);
    this.audio.setDroneIntensity(0);
    this.audio.setChimeGain(0, 250);
    this.cb.onGameOver({ samples: this.samples, scoreEstimate: this.scoreEstimate, bestStreak: this.bestStreak, insideCount: this.insideCount });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    // v6 — release the adaptive drone hold so a navigate-away → re-mount
    // cycle doesn't leave the bed sustaining. finish() already does this
    // on natural game-over; dispose() covers the dismount-before-finish
    // path.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the parallax-starfield layers off the scene so a
    // navigate-away → re-mount doesn't accumulate THREE.Points.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    if (this.samplerTimer) clearInterval(this.samplerTimer);
    for (const t of this.tunerTrail) {
      this.engine.scene.remove(t.mesh);
      t.mat.dispose();
    }
    this.tunerTrailGeo.dispose();
    if (this.ghostLine) {
      this.engine.scene.remove(this.ghostLine);
      this.ghostLine.geometry.dispose();
      this.ghostMat?.dispose();
      this.ghostLine = null;
      this.ghostMat = null;
    }
    if (this.bandMesh2) {
      this.engine.scene.remove(this.bandMesh2);
      this.bandMat2?.dispose();
      this.bandMesh2 = null;
      this.bandMat2 = null;
    }
    this.engine.dispose();
  }
}
